import type { AnswerSource } from '@cfa/pathway-fe-shared/xp';

export interface ExcerptMatch {
  startIndex: number;
  endIndex: number;
  matchedText: string;
  fullExcerpt: string;
  sourceName: string;
  sourceId: string;
}

// Minimum length for a match to be considered valid (avoids matching common short phrases)
const MIN_MATCH_LENGTH = 25;

const isWordChar = (ch: string | undefined): boolean =>
  ch !== undefined && /\w/.test(ch);

const isTrailingPunctuation = (ch: string | undefined): boolean =>
  ch !== undefined && /[.,!?;:]/.test(ch);

/**
 * Extends a match range so it doesn't start or end in the middle of a word.
 * Also absorbs trailing punctuation (e.g. a period at the end of a sentence)
 * so the link icon doesn't leave a lone "." dangling after it.
 */
const snapToWordBoundaries = (
  text: string,
  start: number,
  end: number,
): [number, number] => {
  let s = start;
  let e = end;

  while (s > 0 && isWordChar(text[s - 1]) && isWordChar(text[s])) {
    s--;
  }

  while (e < text.length && isWordChar(text[e]) && isWordChar(text[e - 1])) {
    e++;
  }

  // Absorb trailing whitespace + punctuation (e.g. " .") so a lone period
  // doesn't dangle after the link icon
  let peek = e;
  while (peek < text.length && text[peek] === ' ') {
    peek++;
  }
  if (peek < text.length && isTrailingPunctuation(text[peek])) {
    e = peek + 1;
  }

  return [s, e];
};

/**
 * Finds the longest common substring between two strings
 */
const findLongestCommonSubstring = (str1: string, str2: string): string => {
  const len1 = str1.length;
  const len2 = str2.length;

  // Create a 2D array to store lengths of longest common suffixes
  const dp: number[][] = Array.from({ length: len1 + 1 }, () =>
    Array<number>(len2 + 1).fill(0),
  );

  let maxLength = 0;
  let endIndex = 0;

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        const row = dp[i];
        const previousRow = dp[i - 1];

        if (!row || !previousRow) continue;

        const currentLength = (previousRow[j - 1] ?? 0) + 1;
        row[j] = currentLength;

        if (currentLength > maxLength) {
          maxLength = currentLength;
          endIndex = i;
        }
      }
    }
  }

  return str1.substring(endIndex - maxLength, endIndex);
};

/**
 * Finds all excerpt matches in the response text
 * Returns matches sorted by startIndex
 */
export const findExcerptMatches = (
  responseText: string,
  sources: AnswerSource[],
): ExcerptMatch[] => {
  const matches: ExcerptMatch[] = [];
  const normalizedResponse = responseText.toLowerCase();

  for (const source of sources) {
    if (!source.excerpt) continue;

    const normalizedExcerpt = source.excerpt.toLowerCase();

    // Find the longest common substring between response and excerpt
    const longestMatch = findLongestCommonSubstring(
      normalizedResponse,
      normalizedExcerpt,
    );

    if (longestMatch.length >= MIN_MATCH_LENGTH) {
      const rawStart = normalizedResponse.indexOf(longestMatch);
      if (rawStart !== -1) {
        const rawEnd = rawStart + longestMatch.length;
        const [startIndex, endIndex] = snapToWordBoundaries(
          responseText,
          rawStart,
          rawEnd,
        );

        const matchedText = responseText.substring(startIndex, endIndex);

        matches.push({
          startIndex,
          endIndex,
          matchedText,
          fullExcerpt: source.excerpt,
          sourceName: source.name,
          sourceId: source.id,
        });
      }
    }
  }

  // Sort by startIndex and remove overlapping matches (keep longer ones)
  return resolveOverlappingMatches(matches);
};

/**
 * Resolves overlapping matches by keeping the longer match
 */
const resolveOverlappingMatches = (matches: ExcerptMatch[]): ExcerptMatch[] => {
  if (matches.length <= 1) return matches;

  // Sort by start index, then by length (descending)
  const sorted = [...matches].sort((a, b) => {
    if (a.startIndex !== b.startIndex) {
      return a.startIndex - b.startIndex;
    }
    return b.endIndex - b.startIndex - (a.endIndex - a.startIndex);
  });

  const result: ExcerptMatch[] = [];

  for (const match of sorted) {
    // Check if this match overlaps with the last added match
    const lastMatch = result[result.length - 1];

    if (!lastMatch || match.startIndex >= lastMatch.endIndex) {
      // No overlap, add the match
      result.push(match);
    } else if (
      match.endIndex - match.startIndex >
      lastMatch.endIndex - lastMatch.startIndex
    ) {
      // Current match is longer, replace the last one
      result[result.length - 1] = match;
    }
    // Otherwise, skip this match (it's shorter and overlapping)
  }

  return result;
};
